import React, { useContext, useEffect } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Providers, ProviderState } from '@microsoft/mgt-element';
import { TeamsFxProvider } from '@microsoft/mgt-teamsfx-provider'; //'@microsoft/mgt-teamsfx-provider';
import {
  Person,
  PersonViewType,
  PersonCardInteraction,
} from '@microsoft/mgt-react';
import { Theme } from '@fluentui/react';
import {
  SsoAuthContainer,
  SsoAuthContainerMobile,
  SsoCardContainer,
  SsoContainer,
  SsoFooter,
} from './styles';
import { TeamsFxContext } from '../../Context';
import { isMobile } from 'react-device-detect';
import { Tag } from '../Tag';
import { BitButton } from '../BitButton';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  FluentProvider,
} from '@fluentui/react-components';

type SSOProps = {
  isSSO: boolean;
  setIsSSO: React.Dispatch<React.SetStateAction<boolean>>;
  showVisuals?: boolean;
  theme?: Theme;
  themeName?: string;
};

const SSO: React.FC<SSOProps> = ({
  isSSO,
  setIsSSO,
  showVisuals,
  theme,
  themeName,
}) => {
  const appInsights = useAppInsightsContext();

  const { t } = useTranslation(['sso', 'licencePopups']);

  const [isPopupVisible, setIsPopupVisible] = React.useState(false);

  // microsoftTeams.app.initialize();
  // const [context, setContext] = useState<microsoftTeams.app.Context>();
  // microsoftTeams.app.getContext().then((context) => setContext(context));

  const { teamsUserCredential, hostName, m365MobileAuthenticated } =
    useContext(TeamsFxContext);
  const [loads, setLoading] = React.useState<boolean>(true);
  const [consentNeeded, setConsentNeeded] = React.useState<boolean>(false);
  const scopes = [
    'User.Read',
    'People.Read',
    'User.ReadBasic.All',
    'Presence.Read.All',
    'TeamsActivity.Send',
    'TeamsAppInstallation.ReadForUser',
  ];

  if (teamsUserCredential) {
    const provider = new TeamsFxProvider(teamsUserCredential, scopes);
    Providers.globalProvider = provider;
    Providers.globalProvider.setState(ProviderState.SignedIn);
  }

  useEffect(() => {
    const init = async () => {
      try {
        // if (teamsUserCredential) {
        //const accessToken = (await teamsUserCredential.getToken(scopes))!.token;
        if (Providers.globalProvider.state === ProviderState.SignedIn) {
          const token = await Providers.globalProvider.getAccessToken({
            scopes: scopes,
          });
          if (token) {
            setIsSSO(true);
          } else {
            if (isMobile && hostName === 'Office') {
              //RENATO - Situacao de bug do mobile M365
              setIsSSO(true);
            } else {
              setConsentNeeded(true);
              setIsSSO(false);
              setLoading(false);
            }
          }
        }
      } catch (error: any) {
        if (process.env.REACT_APP_APP_INSIGHTS !== '') {
          appInsights.trackException({
            exception: error,
            properties: { component: 'SSO', location: 'onUseEffect' },
          });
        }
        setConsentNeeded(true);
        setIsSSO(false);
        setLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    //RENATO - Situacao de bug do mobile M365
    const init = async () => {
      try {
        if (isMobile && hostName === 'Office') {
          if (teamsUserCredential) {
            await teamsUserCredential.getToken('');
            setIsSSO(true);
          }
        }
      } catch (error: any) {
        if (process.env.REACT_APP_APP_INSIGHTS !== '') {
          appInsights.trackException({
            exception: error,
            properties: { component: 'SSO', location: 'onUseEffectHostName' },
          });
        }
        setConsentNeeded(true);
        setIsSSO(false);
        setLoading(false);
      }
    };

    init();
  }, [hostName]);

  const consent = async () => {
    try {
      setLoading(true);
      if (teamsUserCredential) {
        await teamsUserCredential.login(scopes);
      }
      Providers.globalProvider.setState(ProviderState.SignedIn);
      setLoading(false);
      setConsentNeeded(false);
      setIsSSO(true);
    } catch (error: any) {
      if (process.env.REACT_APP_APP_INSIGHTS !== '') {
        appInsights.trackException({
          exception: error,
          properties: { component: 'SSO', location: 'onConsent' },
        });
      }
    }
  };

  useEffect(() => {
    !loads && !isSSO && setIsPopupVisible(true);
  }, [isSSO, loads]);

  return (
    <SsoContainer>
      {!isMobile ? (
        <>
          {showVisuals && (
            <>
              <h1>{t('SSO_SETTINGS_HEADER')}</h1>
              <SsoCardContainer
                aria-roledescription="SSO card"
                title="Single Sign-On Status"
              >
                <p className="description">{t('SSO_SETTINGS_DESCRIPTION')}</p>

                <p className="disclaimer">
                  {t('SSO_SETTINGS_DISCLAIMER_STATUS')}
                </p>

                <p className="disclaimer">
                  {t('SSO_SETTINGS_DISCLAIMER_CONTACT')}

                  <a href="mailto:support@bitfractal.com">
                    support@bitfractal.com
                  </a>
                </p>

                <h2>
                  {t('APP_SSO_SETTINGS_PROVIDED')}
                  {!loads &&
                    (isSSO ? (
                      <Tag
                        content={t('SSO_SETTINGS_PROVIDED_DONE')}
                        color="green"
                        variant="outlined"
                      />
                    ) : (
                      <Tag
                        content={t('SSO_SETTINGS_PROVIDED_PENDING')}
                        color="red"
                        variant="outlined"
                      />
                    ))}
                </h2>

                <h2>
                  {t('BOT_SSO_SETTINGS_PROVIDED')}
                  {!loads &&
                    (isSSO ? (
                      <Tag
                        content={t('SSO_SETTINGS_PROVIDED_DONE')}
                        color="green"
                        variant="outlined"
                      />
                    ) : (
                      <Tag
                        content={t('SSO_SETTINGS_PROVIDED_PENDING')}
                        color="red"
                        variant="outlined"
                      />
                    ))}
                </h2>

                <SsoFooter>
                  <span>{t('SSO_SETTINGS_LOGGED_IN_AS')}</span>
                  <Person
                    className="listperson"
                    personQuery="me"
                    view={PersonViewType.twolines}
                    showPresence
                    personCardInteraction={PersonCardInteraction.none}
                    line2Property="email"
                  />
                </SsoFooter>
              </SsoCardContainer>
            </>
          )}
          {!loads && !isSSO && (
            <>
              <SsoAuthContainer>
                <h3>{t('SSO_CLICK_TO_AUTHORIZE')}</h3>
                <BitButton action={consent} label={t('SSO_AUTHORIZE_BUTTON')} />
              </SsoAuthContainer>
              <FluentProvider>
                <Dialog
                  open={isPopupVisible}
                  onOpenChange={(_, data) => setIsPopupVisible(data.open)}
                >
                  <DialogSurface>
                    <DialogBody>
                      <DialogTitle>
                        {t('SSO_POPUP_TITLE', { ns: 'licencePopups' })}
                      </DialogTitle>
                    </DialogBody>
                    <DialogContent>{t('SSO_CLICK_TO_AUTHORIZE')}</DialogContent>
                    <DialogActions style={{ marginTop: '1.2rem' }}>
                      <BitButton
                        label={t('SSO_POPUP_BUTTON', { ns: 'licencePopups' })}
                        action={consent}
                      />
                      <DialogTrigger disableButtonEnhancement>
                        <BitButton
                          label={t('CLOSE_POPUP_BUTTON', {
                            ns: 'licencePopups',
                          })}
                          variant="secondary"
                          action={() => setIsPopupVisible(false)}
                        />
                      </DialogTrigger>
                    </DialogActions>
                  </DialogSurface>
                </Dialog>
              </FluentProvider>
            </>
          )}
        </>
      ) : (
        <>
          {!loads && !isSSO && (
            <SsoAuthContainerMobile>
              <h3>{t('SSO_CLICK_TO_AUTHORIZE')}</h3>
              <h3>{t('SSO_REFRESH_WARNING')}</h3>
              <BitButton action={consent} label={t('SSO_AUTHORIZE_BUTTON')} />
            </SsoAuthContainerMobile>
          )}
        </>
      )}
    </SsoContainer>
  );
};

export default SSO;
